import { DateTime } from 'luxon';
import React from 'react';
import { useSelector } from 'react-redux';

import { COLOR_WEEKEND, HEIGHT_ITEM } from '../const';
import { RootState } from '../store';
import { HeaderType, ModeTimeLineType, RowGantType } from '../types';
import GantGridColumn from './gantGridColumn';
import GantGridRow from './gantGridRow';
import GantGridRowSelectItem from './gantGridRowSelectItem';
import { useGantOperationalHeight } from './hook/useGantOperationalHeight';

interface GantGridProps {
    dates: DateTime[];
}

interface PatternProps {
    width: number;
    height: number;
    lineGap: number;
    dayShift: number;
}

const Patterns: React.FC<PatternProps> = ({ width, height, lineGap, dayShift }) => {
    return (
        <>
            <defs>
                <pattern
                    id="gant-grid-weekends"
                    x={-dayShift * lineGap}
                    patternUnits="userSpaceOnUse"
                    width={lineGap * 7}
                    height={height}
                >
                    {[0, 6].map((index) => (
                        <rect
                            key={index}
                            x={lineGap * index + 1}
                            y={0}
                            width={lineGap - 1}
                            height={height}
                            fill={COLOR_WEEKEND}
                            opacity={0.4}
                        />
                    ))}
                </pattern>
                <pattern id="gant-grid-vert-lines" patternUnits="userSpaceOnUse" width={lineGap} height={height}>
                    <line x1={0} y1={0} x2={0} y2={height} strokeWidth={1} stroke={'#c5c5c5'} />
                </pattern>
                <pattern
                    id="gant-grid-hor-lines"
                    patternUnits="userSpaceOnUse"
                    width={width}
                    height={HEIGHT_ITEM}
                    y={1}
                >
                    <line
                        key={`h-line`}
                        x1={0}
                        y1={HEIGHT_ITEM}
                        x2={width}
                        y2={HEIGHT_ITEM}
                        strokeWidth={1}
                        stroke={'#c5c5c5'}
                    />
                </pattern>
            </defs>
        </>
    );
};

const GantGrid: React.FC<GantGridProps> = ({ dates }) => {
    const headerType = useSelector<RootState, HeaderType>((state) => state.gant.headerType);
    const modeTimeLine = useSelector<RootState, ModeTimeLineType>((state) => state.gant.modeTimeLine);
    const widthLeftHeader = useSelector<RootState, number>((state) => state.gant.widthLeftHeader);
    const availableHeight = useSelector<RootState, number>((state) => state.gant.availableHeight);
    const countVisibleRow = useSelector<RootState, number>((state) => state.gant.countVisibleRow);

    const startDateExtend = useSelector<RootState, string>((state) => state.gant.startDateExtend);
    const endDateExtend = useSelector<RootState, string>((state) => state.gant.endDateExtend);
    const start = DateTime.fromISO(startDateExtend);
    const end = DateTime.fromISO(endDateExtend);
    const fullLengthGantInDays = Math.ceil(end.diff(start, 'days').days) + 1;
    const widthColumn = useSelector<RootState, number>((state) => state.gant.widthColumn);

    const visibleHeight = useGantOperationalHeight();

    let rowType: HeaderType | RowGantType;

    switch (headerType) {
        case 'quarter':
            rowType = 'week';
            break;
        case 'week':
            rowType = 'day';
            break;
        case 'month':
            rowType = modeTimeLine === 'pagination' ? 'day' : 'week';
            break;
        case 'year':
            rowType = 'week';
            break;
        default:
            rowType = 'day';
    }

    const dayShift = DateTime.fromISO(startDateExtend).weekday % 7;

    // TODO: adopt patterns for other modes
    if (modeTimeLine !== 'pagination' && (headerType === 'day' || headerType === 'week')) {
        const div = Math.ceil(visibleHeight / HEIGHT_ITEM);

        return (
            <>
                <Patterns
                    width={fullLengthGantInDays * widthColumn}
                    height={availableHeight}
                    lineGap={20}
                    dayShift={dayShift}
                />
                <rect width="100%" height={visibleHeight} fill="url(#gant-grid-weekends)" />
                <rect width="100%" height={visibleHeight} fill="url(#gant-grid-vert-lines)" />
                {Array.from({ length: div }).map((_, index) => {
                    return (
                        <line
                            key={`h-line-${index}`}
                            x1={0}
                            y1={index * HEIGHT_ITEM}
                            x2={'100%'}
                            y2={index * HEIGHT_ITEM}
                            strokeWidth={0.8}
                            stroke={'#d5d4d4'}
                        />
                    );
                })}
                <GantGridRowSelectItem />
            </>
        );
    }

    return (
        <g transform="translate(0, 0)">
            <g>
                <GantGridColumn
                    dates={dates}
                    rowType={rowType}
                    heightRow={HEIGHT_ITEM}
                    height={countVisibleRow * HEIGHT_ITEM}
                    widthLeftHeader={widthLeftHeader}
                />
            </g>
            <g>
                <GantGridRow />
                <GantGridRowSelectItem />
            </g>
        </g>
    );
};

export default React.memo(GantGrid);
